/**
 * Left sidebar — brand header, the Solutions / Development / Administration
 * sections, footer with the "view as role" picker and the signed-in user card.
 *
 * **Solutions are everyone's** (ADR-0018): Base (Explore, Create, Review,
 * Quality), Validation and Clinical. Roadmap items open an inert
 * `ModulePlaceholder` (ADR-0004) and carry a "Soon" badge, so they read as
 * planned rather than broken.
 *
 * **Development** holds the building surfaces: Integrations, Data browser and
 * Collections are Developer+; Projects is all roles (ADR-0018). The heading
 * shows whenever at least one child does. **Administration** is Developer+ in
 * its entirety — user management and tickets (Administrator), the audit log,
 * documentation — so for a plain
 * `User` the whole section is absent rather than present and disappointing.
 * Placement is not permission: the nav only hides; the routes and the API
 * re-check (a `User` holds `me.read`, `me.update` and `audit.read.self`, and
 * nothing else).
 *
 * **It collapses to an icon rail, not to nothing.** The control that reopens a
 * sidebar has to live somewhere; parking it in the header means the header
 * carries a button for one rare state forever. A rail keeps navigation reachable
 * while giving the canvas back most of the width, and the toggle stays exactly
 * where it was. Every rail item is the same destination as its full-width row,
 * with the label in a tooltip.
 */

import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Divider,
  Group,
  Image,
  NavLink,
  Select,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArchive,
  IconBooks,
  IconBrandAws,
  IconChecklist,
  IconClipboardCheck,
  IconDatabase,
  IconFileImport,
  IconFolderSearch,
  IconFolders,
  IconGavel,
  IconHeadset,
  IconLayoutGrid,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconMessageSearch,
  IconPencilPlus,
  IconPlugConnected,
  IconRobot,
  IconRosetteDiscountCheck,
  IconSearch,
  IconShieldCheck,
  IconShieldHalf,
  IconStack2,
  IconStethoscope,
  IconUserCheck,
  IconTicket,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useMsal } from "@azure/msal-react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useMe } from "@/features/users/useMe";
import { useEffectiveRoles, useRolePreview } from "@/features/auth/RolePreviewContext";

interface ModuleEntry {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** A roadmap item: opens a placeholder and wears a "Soon" badge. */
  soon?: boolean;
  /** Extra prefixes that also mean "you are here" — for an entry whose
   *  destination is a hub with several URLs. Defaults to `path` alone. */
  also?: string[];
}

/** Prefix match on whole segments, so `/projects` does not claim
 *  `/projects-archive`, and a hub's row lights for any of its tabs. */
function isActive(pathname: string, entry: { path: string; also?: string[] }): boolean {
  return [entry.path, ...(entry.also ?? [])].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

interface SolutionGroup {
  label: string;
  icon: React.ReactNode;
  entries: ModuleEntry[];
}

/** The Solutions tree. All roles (ADR-0018) — adding a solution is an entry,
 *  not a nav rewrite. "Release validation" is the risk assessment only. */
function solutionGroups(): SolutionGroup[] {
  return [
    {
      label: "Base",
      icon: <IconStack2 size={18} />,
      entries: [
        { label: "Explore", path: "/solutions/explore", icon: <IconMessageSearch size={18} /> },
        { label: "Create", path: "/solutions/create", icon: <IconPencilPlus size={18} /> },
        { label: "Review", path: "/solutions/review", icon: <IconChecklist size={18} /> },
        {
          label: "Quality",
          path: "/solutions/quality",
          icon: <IconRosetteDiscountCheck size={18} />,
        },
      ],
    },
    {
      label: "Validation",
      icon: <IconShieldCheck size={18} />,
      entries: [
        {
          label: "1st line support",
          path: "/solutions/validation/first-line-support",
          icon: <IconHeadset size={18} />,
        },
        {
          label: "2nd line support",
          path: "/solutions/validation/second-line-support",
          icon: <IconRobot size={18} />,
        },
        {
          label: "Release validation",
          path: "/validation/risk-assessment",
          also: ["/validation/runs", "/validation/risk-assessment"],
          icon: <IconClipboardCheck size={18} />,
        },
        // A tester is any Entra user, and this is their whole view of validation.
        {
          label: "My tests",
          path: "/validation/my-tests",
          icon: <IconChecklist size={18} />,
        },
      ],
    },
    {
      label: "Clinical",
      icon: <IconStethoscope size={18} />,
      entries: [
        {
          label: "eTMF intake",
          path: "/solutions/clinical/etmf-intake",
          icon: <IconFileImport size={18} />,
        },
        {
          label: "eTMF completeness",
          path: "/solutions/clinical/etmf-completeness",
          icon: <IconLayoutGrid size={18} />,
        },
        // eTMF artifacts assigned to the signed-in user for review (ADR-0043).
        {
          label: "Assignments",
          path: "/solutions/clinical/assignments",
          icon: <IconUserCheck size={18} />,
        },
      ],
    },
    {
      // The records archive. Search first: for an end user the archive is
      // something you search, not an inventory you browse; the container list
      // is the records manager's view. Which archive a person can open is a
      // per-container grant, checked server-side — the nav only shows the door.
      label: "Archive",
      icon: <IconArchive size={18} />,
      entries: [
        {
          label: "Search & Retrieve",
          path: "/archive",
          also: ["/archive/search"],
          icon: <IconSearch size={18} />,
        },
        {
          label: "Archives",
          path: "/archive/containers",
          icon: <IconArchive size={18} />,
        },
      ],
    },
  ];
}

/** The connection-backed browsers. Each opens its own picker page; the
 *  connection is chosen inside, not before. */
const BROWSERS: { path: string; label: string }[] = [
  { path: "/browse/veeva", label: "Veeva Vault" },
  { path: "/browse/blob", label: "Azure Blob Storage" },
  { path: "/browse/database", label: "Database" },
  { path: "/browse/cara", label: "Generis CARA" },
  { path: "/browse/s3", label: "AWS S3" },
];

/** The navy the mark is drawn on. Not a theme colour — the theme's scale is
 *  derived from the older two-tone blue, and this is the logo file's own value.
 *  One place, so the header and the rail cannot drift apart. */
const BRAND_NAVY = "#051F45";

function SoonBadge() {
  return (
    <Badge size="xs" variant="light" color="gray">
      Soon
    </Badge>
  );
}

export function NavbarPanel({
  collapsed = false,
  onToggle,
}: {
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const { pathname } = useLocation();
  const { data } = useMe();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { previewRole, setPreviewRole } = useRolePreview();

  const realRoles = data?.roles ?? [];
  const isAdmin = realRoles.includes("administrator");
  // Effective roles honour an admin's "view as role" preview; for everyone
  // else these equal the real roles. Nav visibility uses the effective set so
  // an admin previewing as User sees exactly what a User sees.
  const effectiveRoles = useEffectiveRoles(realRoles);
  // Administration surfaces are Developer+: configuring connections, browsing
  // a connected system directly, user management, the audit log. Every route
  // behind them is RBAC-gated server-side too.
  const isDeveloperPlus =
    effectiveRoles.includes("developer") || effectiveRoles.includes("administrator");
  const showIntegration = isDeveloperPlus;
  // Collections sit beside the other building surfaces and share their gate.
  const showCollections = isDeveloperPlus;
  // Projects are everyone's (ADR-0018) — moving them under Development is
  // placement, not permission. What a User actually *sees* inside is still
  // decided per resource by ownership + grants (ADR-0015), so an empty list is
  // a normal state, not a permission failure.
  const showProjects = true;
  const groups = solutionGroups();
  const showDevelopment = showIntegration || showProjects || showCollections;
  // Administration surfaces: user management is administrator-only; the audit
  // log and documentation are developer + administrator. RBAC-gated
  // server-side too.
  const showUserMgmt = effectiveRoles.includes("administrator");
  const showAudit = isDeveloperPlus;

  const handleSignOut = async () => {
    try {
      await instance.logoutPopup({ account: accounts[0] });
    } finally {
      navigate("/login", { replace: true });
    }
  };

  if (collapsed) {
    return (
      <Rail
        pathname={pathname}
        onToggle={onToggle}
        showProjects={showProjects}
        showIntegration={showIntegration}
        showCollections={showCollections}
        groups={groups}
        showUserMgmt={showUserMgmt}
        showAudit={showAudit}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  return (
    <Stack h="100%" gap={0}>
      <BrandHeader onToggle={onToggle} />

      <Box p="md" className="daq-no-scrollbar" style={{ flex: 1, overflowY: "auto" }}>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
          Solutions
        </Text>
        <Stack gap={2}>
          {/* Closed by default so the sidebar opens tidy; the group holding
              the current page opens itself so you never lose your place. */}
          {groups.map((group) => (
            <NavLink
              key={group.label}
              label={group.label}
              leftSection={group.icon}
              defaultOpened={group.entries.some((entry) => isActive(pathname, entry))}
              variant="light"
            >
              {group.entries.map((entry) => (
                <NavLink
                  key={entry.path}
                  component={RouterLink}
                  to={entry.path}
                  label={entry.label}
                  leftSection={entry.icon}
                  rightSection={entry.soon ? <SoonBadge /> : undefined}
                  active={isActive(pathname, entry)}
                  variant="light"
                />
              ))}
            </NavLink>
          ))}
        </Stack>

        {showDevelopment && (
          <>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mt="lg" mb={6}>
              Development
            </Text>
            <Stack gap={2}>
              {/* One entry, not three. Connected / Roles / Add connection are
                  tabs inside the hub: they are three views of one subject, and
                  as separate nav rows they made the sidebar carry the hub's
                  internal structure. Each keeps its own URL, so a deep link
                  still lands on the right tab. */}
              {showIntegration && (
                <NavLink
                  component={RouterLink}
                  to="/integration/connections"
                  label="Integrations"
                  leftSection={<IconPlugConnected size={18} />}
                  active={pathname.startsWith("/integration")}
                  variant="light"
                />
              )}
              {showProjects && (
                <NavLink
                  component={RouterLink}
                  to="/projects"
                  label="Projects"
                  leftSection={<IconFolders size={18} />}
                  active={pathname.startsWith("/projects")}
                  variant="light"
                />
              )}
              {showIntegration && (
                <NavLink
                  label="Data browser"
                  leftSection={<IconFolderSearch size={18} />}
                  defaultOpened={pathname.startsWith("/browse")}
                  variant="light"
                >
                  {BROWSERS.map((b) => (
                    <NavLink
                      key={b.path}
                      component={RouterLink}
                      to={b.path}
                      label={b.label}
                      active={pathname.startsWith(b.path)}
                      variant="light"
                    />
                  ))}
                </NavLink>
              )}
              {showCollections && (
                <NavLink
                  component={RouterLink}
                  to="/development/collections"
                  label="Collections"
                  leftSection={<IconBooks size={18} />}
                  active={pathname.startsWith("/development/collections")}
                  variant="light"
                />
              )}
            </Stack>
          </>
        )}

        {(showUserMgmt || showAudit) && (
          <>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mt="lg" mb={6}>
              Administration
            </Text>
            <Stack gap={2}>
              {showUserMgmt && (
                <NavLink
                  component={RouterLink}
                  to="/admin/users"
                  label="User management"
                  leftSection={<IconUsersGroup size={18} />}
                  active={pathname.startsWith("/admin/users")}
                  variant="light"
                />
              )}
              {showUserMgmt && (
                <NavLink
                  component={RouterLink}
                  to="/admin/tickets"
                  label="Tickets"
                  leftSection={<IconTicket size={18} />}
                  active={pathname.startsWith("/admin/tickets")}
                  variant="light"
                />
              )}
              {showAudit && (
                <NavLink
                  component={RouterLink}
                  to="/admin/audit"
                  label="Audit log"
                  leftSection={<IconShieldHalf size={18} />}
                  active={pathname.startsWith("/admin/audit")}
                  variant="light"
                />
              )}
            </Stack>
          </>
        )}
      </Box>

      <Divider />

      <Stack gap="xs" p="md">
        <Text size="xs" tt="uppercase" fw={600} c="dimmed">
          View as role
        </Text>
        <Select
          value={capitalise(previewRole ?? primaryRole(realRoles))}
          data={["Administrator", "Developer", "User"]}
          onChange={(v) => {
            if (!v) return;
            const lower = v.toLowerCase();
            // Selecting the admin's own role clears the preview.
            setPreviewRole(lower === "administrator" ? null : lower);
          }}
          disabled={!isAdmin}
          allowDeselect={false}
          size="xs"
          aria-label={isAdmin ? "View as role (preview)" : "View as role (admins only)"}
        />
        {previewRole && previewRole !== "administrator" && (
          <Text size="xs" c="orange.7">
            Previewing as {previewRole} — pick Administrator to exit.
          </Text>
        )}

        {data && (
          <UserCard
            displayName={data.display_name}
            role={primaryRole(data.roles)}
            onSignOut={() => void handleSignOut()}
          />
        )}
      </Stack>
    </Stack>
  );
}

function BrandHeader({ onToggle }: { onToggle?: () => void }) {
  return (
    <Group
      gap="sm"
      align="center"
      wrap="nowrap"
      p="md"
      style={{
        // The mark's own navy, not the old indigo gradient: the white wordmark
        // is drawn for this colour, and a near-miss behind a logo reads as a
        // printing error rather than a gradient.
        background: BRAND_NAVY,
        color: "white",
      }}
    >
      <UnstyledButton
        component={RouterLink}
        to="/me"
        style={{ flex: 1, minWidth: 0, color: "inherit" }}
      >
        {/* The 2026 brand wordmark: white DAQUMA, blush-pink Q, on the navy. */}
        <Image
          src="/logo-daquma-white.png"
          alt="DAQUMA"
          h={30}
          w="auto"
          fit="contain"
          style={{ display: "block" }}
        />
      </UnstyledButton>
      {onToggle && (
        <Tooltip label="Collapse navigation" position="right">
          <ActionIcon
            variant="subtle"
            color="gray.2"
            onClick={onToggle}
            aria-label="Collapse navigation"
          >
            <IconLayoutSidebarLeftCollapse size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

/** The collapsed sidebar: the same destinations, icon-only, labels in tooltips. */
function Rail({
  pathname,
  onToggle,
  showProjects,
  showIntegration,
  showCollections,
  groups,
  showUserMgmt,
  showAudit,
  onSignOut,
}: {
  pathname: string;
  onToggle?: () => void;
  showProjects: boolean;
  showIntegration: boolean;
  showCollections: boolean;
  groups: SolutionGroup[];
  showUserMgmt: boolean;
  showAudit: boolean;
  onSignOut: () => void;
}) {
  // Flattened: a rail has no room for a heading or a disclosure, so what is a
  // nested group at full width becomes its leaves here. Each keeps its own
  // route, so nothing is reachable only by expanding first.
  const items: (ModuleEntry & { show: boolean })[] = [
    ...groups.flatMap((g) => g.entries.map((e) => ({ ...e, show: true }))),
    {
      label: "Integrations",
      path: "/integration/connections",
      also: ["/integration"],
      icon: <IconPlugConnected size={18} />,
      show: showIntegration,
    },
    {
      label: "Projects",
      path: "/projects",
      icon: <IconFolders size={18} />,
      show: showProjects,
    },
    {
      label: "Approvals",
      path: "/projects/approvals",
      icon: <IconGavel size={18} />,
      show: showProjects,
    },
    {
      label: "Veeva Vault browser",
      path: "/browse/veeva",
      icon: <IconFolderSearch size={18} />,
      show: showIntegration,
    },
    {
      label: "Blob storage browser",
      path: "/browse/blob",
      icon: <IconDatabase size={18} />,
      show: showIntegration,
    },
    {
      label: "S3 browser",
      path: "/browse/s3",
      icon: <IconBrandAws size={18} />,
      show: showIntegration,
    },
    {
      label: "Database browser",
      path: "/browse/database",
      icon: <IconDatabase size={18} />,
      show: showIntegration,
    },
    {
      label: "Collections",
      path: "/development/collections",
      icon: <IconBooks size={18} />,
      show: showCollections,
    },
    {
      label: "User management",
      path: "/admin/users",
      icon: <IconUsersGroup size={18} />,
      show: showUserMgmt,
    },
    {
      label: "Tickets",
      path: "/admin/tickets",
      icon: <IconTicket size={18} />,
      show: showUserMgmt,
    },
    {
      label: "Audit log",
      path: "/admin/audit",
      icon: <IconShieldHalf size={18} />,
      show: showAudit,
    },
  ];

  const shown = items.filter((i) => i.show);
  // Longest match wins. `/projects/approvals` sits under `/projects`, so a
  // plain prefix test would light both and the rail would claim you are in two
  // places at once. `also` lets a hub's row match any of its tabs.
  const activePath = shown
    .filter((i) => isActive(pathname, i))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  return (
    <Stack h="100%" gap={0} align="center">
      <Stack gap={4} align="center" py="sm">
        <UnstyledButton component={RouterLink} to="/me" aria-label="DAQUMA home">
          <Image src="/logo-daquma-badge.png" alt="DAQUMA" h={32} w={32} fit="contain" />
        </UnstyledButton>
        {onToggle && (
          <Tooltip label="Expand navigation" position="right">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onToggle}
              aria-label="Expand navigation"
            >
              <IconLayoutSidebarLeftExpand size={18} />
            </ActionIcon>
          </Tooltip>
        )}
      </Stack>

      <Divider w="100%" />

      <Stack
        gap={4}
        align="center"
        py="sm"
        className="daq-no-scrollbar"
        style={{ flex: 1, overflowY: "auto" }}
      >
        {shown.map((i) => (
          <Tooltip key={i.path} label={i.soon ? `${i.label} · Soon` : i.label} position="right">
            <ActionIcon
              component={RouterLink}
              to={i.path}
              size="lg"
              variant={i.path === activePath ? "light" : "subtle"}
              color={i.path === activePath ? "brand" : "gray"}
              aria-label={i.label}
            >
              {i.icon}
            </ActionIcon>
          </Tooltip>
        ))}
      </Stack>

      <Divider w="100%" />

      <Stack gap={4} align="center" py="sm">
        <Tooltip label="Sign out" position="right">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={onSignOut}
            aria-label="Sign out"
          >
            <IconLogout size={18} />
          </ActionIcon>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

interface UserCardProps {
  displayName: string;
  role: string;
  onSignOut: () => void;
}

function UserCard({ displayName, role, onSignOut }: UserCardProps) {
  return (
    <Group gap="xs" wrap="nowrap" align="center" mt={4} justify="space-between">
      <Group gap="xs" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
        <Avatar size="sm" color="brand" radius="xl">
          {initials(displayName)}
        </Avatar>
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>
            {displayName}
          </Text>
          <Text size="xs" c="dimmed" tt="capitalize">
            {role}
          </Text>
        </Box>
      </Group>
      <Group gap={2} wrap="nowrap">
        {/* Theme and Settings live in the header, beside the bell — one copy of
            each, always on screen. Sign out stays with the person it signs out. */}
        <Tooltip label="Sign out">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onSignOut}
            aria-label="Sign out"
          >
            <IconLogout size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function capitalise(role: string): string {
  if (!role) return "";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function primaryRole(roles: string[]): string {
  // Priority: administrator > developer > user > the first whatever we got.
  if (roles.includes("administrator")) return "administrator";
  if (roles.includes("developer")) return "developer";
  if (roles.includes("user")) return "user";
  return roles[0] ?? "—";
}
